/* @flow */
import 'isomorphic-fetch'
import { API, createDefaultHeaders } from '../../util/api'
import { DEFAULT_ZONE_RUN_DURATION } from '../../util/zone'
import { isInvalid } from '@rachio-npm/com-rachio-web-core/util/validation'
import { isShareCode } from '../../util/location'
import * as actionCreators from './index'
import * as Schema from './schemas'
import changeCase from 'change-object-case'
import moment from 'moment'

// Types
import type { DateObject, DateOrTimeObject } from '../../util/time'
import type { Dispatch, ThunkPromise } from '../../util/redux'
import type { UpdateZoneFlowRequest } from './types'
import {
  getProtoDateObjectFromMomentInstance,
  createControllerMoment
} from '../../util/time'
import type { GetWateringSummaryObj } from '../device'

/**
 * Additional methods around zone functionality
 * @module ducks/zone/methods
 */

/**
 * Get `run` config
 * @param  {string} authToken - Auth token OR share code
 * @param  {string} deviceId - Device ID
 * @param  {number} zoneNumber - Zone number
 * @param  {number} duration - Zone run duration
 * @returns {object} - Run call config
 */
const runModes = (
  authToken: string,
  deviceId: string,
  zoneNumber: number,
  duration: number
) => {
  const reqObject = { deviceId, runs: [{ zoneNumber, duration }] }
  const props = {
    limitedAccess: {
      headers: {
        ...createDefaultHeaders(),
        'Content-Type': 'application/json'
      },
      url: 'device/setManualScheduleQR',
      reqData: changeCase.snakeKeys(
        { ...reqObject, code: authToken },
        { recursive: true, arrayRecursive: true }
      )
    },
    fullAccess: {
      headers: {
        ...createDefaultHeaders(authToken),
        'Content-Type': 'application/json'
      },
      url: 'device/setManualSchedule',
      reqData: changeCase.snakeKeys(reqObject, {
        recursive: true,
        arrayRecursive: true
      })
    }
  }

  if (isShareCode(authToken)) {
    return props.limitedAccess
  } else {
    return props.fullAccess
  }
}

/**
 * Run Zone
 * @param {string} authToken - User Auth Token
 * @param {string} deviceId - Device ID
 * @param {number} zoneNumber - Zone Number
 * @param {number} [duration=module:util/zone.DEFAULT_ZONE_RUN_DURATION] - Duration
 * @returns {function} - Dispatch function
 */
export const run = (
  authToken: string,
  deviceId: string,
  zoneNumber: number,
  duration: number = DEFAULT_ZONE_RUN_DURATION
) => {
  return function() {
    const mode = runModes(authToken, deviceId, zoneNumber, duration)
    const Api = new API()
    const headers = mode.headers

    // Return API call
    return Api.call(mode.url, {
      method: 'POST',
      headers: headers,
      body: JSON.stringify(mode.reqData)
    }).then(response => response.json())
  }
}

/**
 * Get zone default - This is only used in Containers/Zone/Edit to update
 * components state.
 * @param {string} userAuthToken - User's authorization token
 * @param {string} zoneId - Zone id
 * @returns {object} Default zone advanced data
 */
export const getDefault = (userAuthToken: string, zoneId: string) =>
  function(dispatch: Function) {
    const Api = new API()
    const reqObj = {
      method: 'GET',
      headers: new Headers({
        'Content-Type': 'application/json',
        Authorization: 'Bearer ' + userAuthToken
      })
    }

    dispatch(actionCreators.zoneGetDefaultInit(zoneId))

    return Api.call(`zone/getZoneDefault/${zoneId}`, reqObj)
      .then(response => response.json())
      .then(response => {
        dispatch(actionCreators.zoneGetDefaultComplete(response))
        return response
      })
      .catch(err => err)
  }

// Water Journal //////////////////////////////////////////////////////////////

type WaterJournalEvent = {|
  currentMoistureLevel: number, // eg: 0.5200788
  currentMoisturePct: number, // eg: 1
  irrigationData: {|
    automatedRuns: number, // eg: 0
    flexRuns: number, // eg: 0
    manualRuns: number, // eg: 0
    userEdit: number // eg: 0
  |},
  isMetric: boolean, // eg: false
  localDate: DateObject,
  timeZone: string, // eg: "America/Anchorage"
  weatherStationData: {|
    cropEvapotranspiration: number, // eg: 0
    precipitation: number // eg: 0
  |}
|}

type SetWaterJournalDataResponse = {|
  event: WaterJournalEvent
|}

/**
 * Get zone water journal data
 * @param {string} authToken - User Auth Token
 * @param {string} zoneId - Zone ID,
 * @param {object} startDate - { year, month, day }; defaults to 2 weeks ago
 * @param {object} endDate - { year, month, day }; defaults to 2 weeks from now
 * @returns {function} - Dispatch function
 */
export const getWaterJournalData = (
  authToken: string,
  zoneId: string,
  startDate?: ?DateObject,
  endDate?: ?DateObject
) => {
  return function(dispatch: Function) {
    let resolvedStartDate = startDate
    let resolvedEndDate = endDate

    if (!resolvedStartDate) {
      const mom = moment().subtract(2, 'weeks')
      resolvedStartDate = {
        year: mom.year(),
        month: mom.month() + 1,
        day: mom.date()
      }
    }

    if (!resolvedEndDate) {
      const mom = moment().add(2, 'weeks')
      resolvedEndDate = {
        year: mom.year(),
        month: mom.month() + 1,
        day: mom.date()
      }
    }

    const reqData = changeCase.snakeKeys(
      { startDate: resolvedStartDate, endDate: resolvedEndDate },
      { recursive: true, arrayRecursive: true }
    )

    const Api = new API()

    dispatch(
      actionCreators.getWaterJournalInit({
        zoneId,
        startDate: resolvedStartDate,
        endDate: resolvedEndDate
      })
    )

    // Return API call
    return Api.call(`events/waterjournal/zone/${zoneId}`, {
      method: 'POST',
      headers: {
        ...createDefaultHeaders(authToken),
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(reqData)
    })
      .then(response => response.json())
      .then(data => {
        dispatch(
          actionCreators.getWaterJournal({
            id: zoneId,
            waterJournal: data.waterEntries
          })
        )
        return dispatch(
          actionCreators.getWaterJournalComplete({
            id: zoneId,
            waterJournal: data.waterEntries
          })
        )
      })
  }
}

/**
 * @summary Set water journal data
 * @description Takes either an exact moisture level or a moisture percentage.
 *              `moisturePercent` is used only if `moistureLevel` is not a number.
 * @param {string} authToken - User Auth Token
 * @param {string} zoneId - Zone ID
 * @param {object} date - { year, month, day }
 * @param {[number]} moistureLevel - Moisture level
 * @param {[number]} moisturePercent - Moisture percent (0-1)
 * @returns {function} - Dispatch function
 */
export const setWaterJournalData = (
  authToken: string,
  zoneId: string,
  date: DateObject, // Date date = 3;
  moistureLevel?: ?number, // float moisture_level = 4;
  moisturePercent?: number //  float moisture_percent = 5;
) => {
  return function(dispatch: Function) {
    const reqData = changeCase.snakeKeys(
      {
        zoneId,
        date,
        ...(typeof moistureLevel === 'number'
          ? { moistureLevel }
          : { moisturePercent })
      },
      { recursive: true }
    )

    const Api = new API()

    dispatch(
      actionCreators.setWaterJournalInit({
        zoneId,
        date,
        moistureLevel,
        moisturePercent
      })
    )

    // Return API call
    return Api.call('events/postMoistureLevelOverride', {
      method: 'PUT',
      headers: {
        ...createDefaultHeaders(authToken),
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(reqData)
    })
      .then(response => response.json())
      .then((data: SetWaterJournalDataResponse) => {
        dispatch(actionCreators.setWaterJournal({ ...data.event, zoneId }))
        return dispatch(
          actionCreators.setWaterJournalComplete({ ...data.event, zoneId })
        )
      })
      .catch(err => actionCreators.setWaterJournalComplete(err))
  }
}

// Flow Features //////////////////////////////////////////////////////////////

/**
 * Dismiss an alert by zoneId
 * @param  {string} userAuthToken - User auth token
 * @param  {string} zoneId - Zone ID
 * @param  {array} alertIds - array of alert ids to dismiss
 * @return {promise} - Array of Alerts
 */
export const dismissAlerts = (
  userAuthToken: string,
  zoneId: string,
  alertIds: Array<string>
) => {
  const Api = new API()
  return function(dispatch: Dispatch): Promise<*> {
    const rawReqBody: Object = { zoneId, alertIds }
    const reqBody = changeCase.snakeKeys(rawReqBody, { recursive: true })
    const error = isInvalid(Schema.DismissAlertsRequest, reqBody)
    const reqData = {
      method: 'POST',
      headers: {
        ...createDefaultHeaders(userAuthToken),
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(reqBody)
    }

    dispatch(actionCreators.dismissAlertsInit(reqBody))

    if (error) {
      return Promise.reject(
        dispatch(actionCreators.dismissAlertsComplete(error))
      )
    } else {
      // Return API call
      return Api.call('device/dismissAlert/', reqData)
        .then(response => response.json())
        .then(json => dispatch(actionCreators.dismissAlertsComplete(json)))
        .catch(err => dispatch(actionCreators.dismissAlertsComplete(err)))
    }
  }
}

/**
 * Update the flow settings for a zone
 * @param  {string} userAuthToken - User auth token
 * @param  {object} updateZoneFlowRequest - UpdateZoneFlowRequest
 * @return {promise} - Array of Alerts
 */
export const updateZoneFlow = (
  userAuthToken: string,
  updateZoneFlowRequest: UpdateZoneFlowRequest
) => {
  const Api = new API()
  return function(dispatch: Dispatch): Promise<*> {
    const reqBody = changeCase.snakeKeys(updateZoneFlowRequest, {
      recursive: true
    })
    const error = isInvalid(Schema.UpdateZoneFlowRequest, reqBody)
    const reqData = {
      method: 'POST',
      headers: {
        ...createDefaultHeaders(userAuthToken),
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(reqBody)
    }

    dispatch(actionCreators.updateZoneFlowInit(reqBody))

    if (error) {
      return Promise.reject(
        dispatch(actionCreators.updateZoneFlowComplete(error))
      )
    } else {
      // Return API call
      return Api.call('device/updateZoneFlow/', reqData)
        .then(response => response.json())
        .then(json => dispatch(actionCreators.updateZoneFlowComplete(json)))
        .catch(err => dispatch(actionCreators.updateZoneFlowComplete(err)))
    }
  }
}

/**
 * Start a calibration zone run on a zone
 * @param  {string} userAuthToken - User auth token
 * @param  {string} zoneId - zoneId
 * @return {promise} - int32 manual_schedule_id
 */
export const startZoneCalibration = (userAuthToken: string, zoneId: string) => {
  const Api = new API()
  return function(dispatch: Dispatch): Promise<*> {
    const reqBody = changeCase.snakeKeys({ zoneId })
    const error = isInvalid(Schema.StartZoneCalibrationRequest, reqBody)

    const reqData = {
      method: 'POST',
      headers: {
        ...createDefaultHeaders(userAuthToken),
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(reqBody)
    }

    dispatch(actionCreators.startCalibrationInit({ zoneId }))

    if (error) {
      return Promise.reject(error)
    } else {
      // Return API call
      return Api.call('device/startZoneCalibration/', reqData)
        .then(response => response.json())
        .then(() =>
          dispatch(actionCreators.startCalibrationComplete({ zoneId }))
        )
        .catch(err => dispatch(actionCreators.startCalibrationComplete(err)))
    }
  }
}

/**
 * Stop a calibration zone run on a zone
 * @param  {string} userAuthToken - User auth token
 * @param  {string} zoneId - zoneId
 * @return {promise} - Empty
 */
export const stopZoneCalibration = (userAuthToken: string, zoneId: string) => {
  const Api = new API()
  return function(dispatch: Dispatch): Promise<*> {
    const reqBody = changeCase.snakeKeys({ zoneId })
    const error = isInvalid(Schema.StartZoneCalibrationRequest, reqBody)

    const reqData = {
      method: 'POST',
      headers: {
        ...createDefaultHeaders(userAuthToken),
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(reqBody)
    }

    dispatch(actionCreators.stopCalibrationInit({ zoneId }))

    if (error) {
      return Promise.reject(error)
    } else {
      // Return API call
      return Api.call('device/stopZoneCalibration/', reqData)
        .then(response => response.json())
        .then(() =>
          dispatch(actionCreators.stopCalibrationComplete({ zoneId }))
        )
        .catch(err => dispatch(actionCreators.stopCalibrationComplete(err)))
    }
  }
}

/**
 * get watering usage data for all types; used for watering usage graph
 * start and end dates need to be converted from 0 to 1 based month index
 * @param {string} token user's auth token
 * @param {string} deviceId device Id
 * @param {string} zoneId - zoneId
 * @param {string} timeZone - time zone string
 * @param {number} offset offset in months from current month, going back
 * @return {promise} resolving to the dispatch function
 */
export function getMonthlyZoneWateringSummary(
  token: string = '',
  deviceId: string,
  zoneId: string,
  timeZone: string,
  offset: number = 0
) {
  return function(dispatch: Dispatch): Promise<Dispatch> {
    const Api = new API()

    // get 1 month of usage history
    // trying to make as few moment calls as possible for performance
    const endMoment =
      offset === 0
        ? createControllerMoment(timeZone)
        : createControllerMoment(timeZone)
            .subtract(offset, 'months')
            .endOf('month')

    const endDate = getProtoDateObjectFromMomentInstance(endMoment)
    const startDate = getProtoDateObjectFromMomentInstance(
      endMoment.startOf('month')
    )

    const requestData = {
      deviceId,
      zoneId,
      startDate,
      endDate
    }

    const request = {
      method: 'PUT',
      headers: {
        ...createDefaultHeaders(token),
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(
        changeCase.snakeKeys(requestData, { recursive: true })
      )
    }

    dispatch(actionCreators.getWateringSummaryInit({ zoneId, offset }))
    return Api.call('events/getWateringSummaryForZone', request)
      .then(response => response.json())
      .then(json => {
        return dispatch(
          actionCreators.getWateringSummaryComplete({
            zoneId,
            offset,
            wateringSummary: json
          })
        )
      })
      .catch(err =>
        dispatch(
          actionCreators.getWateringSummaryComplete({ ...err, zoneId, offset })
        )
      )
  }
}

export type GetZoneWateringSummaryObj = GetWateringSummaryObj & {
  zoneId: string
}
/**
 * get watering usage data for all types; used for watering usage graph
 * start and end dates need to be converted from 0 to 1 based month index
 * @param {string} token user's auth token
 * @param {string} deviceId device Id
 * @param {string} zoneId - zoneId
 * @param {Object | string} startDate - a moment readable time string or object
 * @param {string} timeZone - timezone string
 * @return {promise} resolving to the dispatch function
 */
export function getZoneWateringSummaryForCSV(
  token: string = '',
  deviceId: string,
  zoneId: string,
  startDate: DateOrTimeObject | string,
  timeZone: string
) {
  return function(dispatch: Dispatch): ThunkPromise<GetZoneWateringSummaryObj> {
    const Api = new API()

    const endDate = getProtoDateObjectFromMomentInstance(
      createControllerMoment(timeZone)
    )
    const start = getProtoDateObjectFromMomentInstance(
      createControllerMoment(timeZone, startDate)
    )
    const requestData = {
      deviceId,
      zoneId,
      startDate: start,
      endDate
    }

    const request = {
      method: 'PUT',
      headers: {
        ...createDefaultHeaders(token),
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(
        changeCase.snakeKeys(requestData, { recursive: true })
      )
    }

    dispatch(
      actionCreators.getZoneWateringSummaryForCSVInit({ zoneId, startDate })
    )
    return Api.call('events/getWateringSummaryForZone', request)
      .then(response => response.json())
      .then(json => {
        return dispatch(
          actionCreators.getZoneWateringSummaryForCSVComplete({
            zoneId,
            startDate: start,
            endDate,
            wateringSummary: json
          })
        )
      })
      .catch(err =>
        dispatch(
          actionCreators.getZoneWateringSummaryForCSVComplete({
            ...err,
            zoneId,
            startDate
          })
        )
      )
  }
}
